import styled from "styled-components";
import { IFFM, IRack, SelectOption, IULD, StationDoor, TransferCargo, TransferTruck, UnitedPickupDropoffData } from "../../../../globals/interfaces";
import apiClient, { apiClientEosNative } from "../../../../apiClient";
import useSWR from "swr";
import useUser from "../../../../customHooks/useUser";
import VirtualTable from "../../../custom/VirtualTable";
import { useEffect, useMemo, useState } from "react";
import Select from 'react-select';
import { formatDatetime, formatEmail, getQrCodeUrl, getTsDate, getUnitedDate, notify, print, safeGet } from "../../../../utils";
import _ from "lodash";
import ScaleLoader from 'react-spinners/ScaleLoader';
import GenerateTruckManifest from "./GenerateTruckManifest";
import useStationInfo from "../../../reporting/breakdownProgress/useStationInfo";
import useLoading from "../../../../customHooks/useLoading";
import { useRecoilValue } from "recoil";
import { unitedScannerIdState } from "../../../../globals/atoms";
import ReactTooltip from 'react-tooltip';

import { Button } from "reactstrap";

const STATUS = ['AVAILABLE', 'OPENED', 'CLOSED', 'DEPARTED'];

type UpdateTruck = (data: Partial<TransferTruck>) => Promise<void>;
type ConstantUnitedData = Pick<UnitedPickupDropoffData, 'userId' | 'name' | 'scanDateTime' | 'scannerId' | 'scannedLoc'>;

interface UldWithAwbs extends IULD {
    loadedBy: string;
    awbs: Array<IRack>;
}

type TruckDataMap = Record<string, UldWithAwbs>;

const fallbackData = {
    trucks: [],
    dockDoors: []
};

const getTrucks = async (key: string): Promise<{
    trucks: Array<TransferTruck>,
    dockDoors: Array<StationDoor>;
}> => {
    try {
        const res = await apiClient.get(key);
        return res.data;
    } catch (err) {
        alert(err);
        return fallbackData;
    }
}

export default function TruckBuildup({
    ready
}: {
    ready: boolean
}) {

    const { user } = useUser();
    const stationInfo = useStationInfo();
    const { setLoading } = useLoading();
    const {
        data,
        mutate
    } = useSWR(`/buildup/trucks/${user.s_unit}`, getTrucks, {
        refreshInterval: 15000,
        fallbackData
    });
    const scannerId = useRecoilValue(unitedScannerIdState);

    const [truckDataMap, setTruckDataMap] = useState<TruckDataMap>({});
    const [truckGuid, setTruckGuid] = useState<string>();
    const [cargoData, setCargoData] = useState<Partial<TransferCargo>>({
        loadedBy: '',
        dockDoor: undefined
    });
    const [isLoadingCargo, setIsLoadingCargo] = useState(false);  // NEW: Loading state

    const selectedTruck = data.trucks.find(truck => truck.guid === truckGuid);
    const truckStatus = safeGet(selectedTruck, 'status', '');

    // NEW: Clear state when selecting a different truck
    const handleSelectTruck = (guid: string) => {
        if (guid !== truckGuid) {
            setTruckDataMap({});  // Clear previous truck's data
            setCargoData({ loadedBy: '', dockDoor: undefined });
        }
        setTruckGuid(guid);
    };

    useEffect(() => {
        const getTruckCargo = async (guid: string) => {
            setIsLoadingCargo(true);  // NEW: Set loading state
            setTruckDataMap({});      // NEW: Clear stale data immediately

            try {
                const res = await apiClient.get(`/buildup/truck-cargo/${guid}`);
                const { dataMap, cargoData } = res.data;

                // NEW: Verify we're still on the same truck (prevent race condition)
                setTruckGuid(currentGuid => {
                    if (currentGuid === guid) {
                        setTruckDataMap(dataMap || {});
                        setCargoData(cargoData || { loadedBy: '', dockDoor: undefined });
                    }
                    return currentGuid;
                });
            } catch (err) {
                alert(err);
                setTruckDataMap({});  // NEW: Clear on error
                setCargoData({ loadedBy: '', dockDoor: undefined });
            } finally {
                setIsLoadingCargo(false);  // NEW: Clear loading state
            }
        }
        if (truckGuid && truckStatus) {
            getTruckCargo(truckGuid);
        }
    }, [truckGuid, truckStatus]);

    const updateTruckDoor: UpdateTruck = async (transferTruckData: Partial<TransferTruck>) => {
        if (!selectedTruck) return;

        const newTruckData: Partial<TransferTruck> = {
            id: selectedTruck.id,
            modifiedBy: user.s_email,
            modifiedAt: getTsDate(),
            guid: selectedTruck.guid,
            ...transferTruckData
        }

        try {
            setLoading(true);
            const res = await apiClientEosNative.put(`/truck-buildup/update`, newTruckData);
            mutate();
            notify('Updated');
        } catch (err) {
            alert(err);
        } finally {
            setLoading(false);
        }
    }

    const updateTruck: UpdateTruck = async (transferTruckData: Partial<TransferTruck>) => {
        if (!selectedTruck) return;

        // NEW: Validate before departure
        if (transferTruckData.status === 'DEPARTED') {
            if (isLoadingCargo) {
                notify('Please wait for cargo data to load', 'error');
                return;
            }

            if (!truckDataMap || Object.keys(truckDataMap).length === 0) {
                notify('No cargo data found for this truck. Please refresh and try again.', 'error');
                return;
            }

            // NEW: Verify we have AWBs in the dataMap
            const totalAwbs = Object.values(truckDataMap).reduce(
                (sum, uld) => sum + (uld.awbs?.length || 0), 0
            );

            if (totalAwbs === 0) {
                notify('No AWBs found in cargo data. Cannot depart empty truck.', 'error');
                return;
            }

            transferTruckData.dockDoor = null;
            transferTruckData.agent = null;
        }

        const newTruckData: Partial<TransferTruck> = {
            id: selectedTruck.id,
            modifiedBy: user.s_email,
            modifiedAt: getTsDate(),
            guid: selectedTruck.guid,
            ...transferTruckData
        }

        const postData: {
            cargoData: Partial<TransferCargo>;
            newTruckData: Partial<TransferTruck>,
            dataMap: Record<string, UldWithAwbs>;
            unitedData: ConstantUnitedData;
            s_unit: string;
        } = {
            cargoData,
            newTruckData,
            dataMap: undefined,
            unitedData: undefined,
            s_unit: user.s_unit
        }

        const departed = transferTruckData.status === 'DEPARTED';
        if (departed) {
            postData.dataMap = truckDataMap;
            postData.unitedData = {
                userId: user.additionalData.unitedVNumber,
                name: user.displayName,
                scanDateTime: getUnitedDate(),
                scannerId,
                scannedLoc: ''
            };

            // NEW: Log what we're sending for debugging
            // console.log('[DEPART] Sending departure request:', {
            //     truckGuid: selectedTruck.guid,
            //     truckName: selectedTruck.name,
            //     dataMapKeys: Object.keys(truckDataMap),
            //     totalAwbs: Object.values(truckDataMap).reduce(
            //         (sum, uld) => sum + (uld.awbs?.length || 0), 0
            //     )
            // });
        }

        try {
            setLoading(true);
            const res = await apiClientEosNative.put(`/truck-buildup/depart`, postData);
            mutate();
            notify('Updated');
            if (departed && res.data && res.data.length) {
                const data: Array<IFFM> = res.data;
                const s_flight_id = _.get(data, '[0].s_flight_id', '');
                const qrCodeUrl = await getQrCodeUrl(s_flight_id);
                print(<GenerateTruckManifest
                    data={data}
                    stationInfo={stationInfo}
                    qrCodeUrl={qrCodeUrl}
                />)
            }
        } catch (err) {
            alert(err);
        } finally {
            setLoading(false);
        }
    }

    const departMailTruck = async () => {
        try {
            const now = getTsDate();
            await apiClient.post(`/depart-mail-truck`, {
                time: now,
                date: formatDatetime(now, true),
                user: user.s_email
            });
            notify('Truck departed');
        } catch (err) {
            alert(err);
        }
    }

    const generateTestData = async () => {
        await apiClient.post(`/buildup/test`);
    }

    if (!ready) {
        return (
            <LoadingUnitedRegistration />
        );
    }

    return (
        <Container>
            <ReactTooltip />
            <TrucksContainer>
                {
                    user.s_unit === 'TBSB1' && (
                        <Button onClick={generateTestData}>Test Data</Button>
                    )
                }
                <FlexContainer>
                    <h3>Select Trailer</h3>
                    <MailIcon
                        className={'fa-duotone fa-solid fa-envelopes-bulk text-success hover'}
                        data-tip={'Depart mail truck now'}
                        onClick={departMailTruck}
                    />
                </FlexContainer>
                <VirtualTable
                    data={data.trucks}
                    mapping={[{
                        name: 'Trailer',
                        value: 'name'
                    }, {
                        name: 'Status',
                        value: 'status'
                    }, {
                        name: 'Door',
                        value: 'dockDoor',
                        number: true
                    }]}
                    numRows={10}
                    handleClick={(item: TransferTruck) => handleSelectTruck(item.guid)}
                />
            </TrucksContainer>
            <TruckAwbsContainer>
                <TruckAwbs
                    selectedTruck={selectedTruck}
                    truckGuid={truckGuid}
                    truckDataMap={truckDataMap}
                    dockDoors={data.dockDoors}
                    updateTruck={updateTruck}
                    updateTruckDoor={updateTruckDoor}
                    cargoData={cargoData}
                    isLoadingCargo={isLoadingCargo}
                />
            </TruckAwbsContainer>
        </Container>
    );
}

const TruckAwbs = ({
    selectedTruck,
    truckGuid,
    truckDataMap,
    dockDoors,
    updateTruck,
    updateTruckDoor,
    cargoData,
    isLoadingCargo
}: {
    selectedTruck: TransferTruck | undefined;
    truckGuid: string | undefined;
    truckDataMap: TruckDataMap;
    dockDoors: Array<StationDoor>;
    updateTruck: UpdateTruck;
    updateTruckDoor: UpdateTruck;
    cargoData: Partial<TransferCargo>;
    isLoadingCargo: boolean;
}) => {

    if (!truckGuid || !selectedTruck) {
        return null;
    }

    const selectedDoor = selectedTruck ? {
        label: `Dock Door ${selectedTruck.dockDoor || 'None'}`,
        value: selectedTruck.dockDoor
    } : {
        label: `Select Dock Door`,
        value: null
    }

    const data = [];

    Object.keys(truckDataMap).map(key => {
        data.push(...truckDataMap[key].awbs);
    });

    const doorOptions: Array<SelectOption> = [
        {
            label: 'Dock Door (None)',
            value: null,
        },
        ...dockDoors.map(item => {
            const busy = item.s_status === 'BUSY';
            return {
                label: `${item.s_dock_door} ${busy ? '(BUSY)' : ''}`,
                value: item.s_dock_door,
                isDisabled: busy
            }
        })
    ];

    const getStatusOptions = (status: string): Array<SelectOption> => {
        if (status === 'CLOSED') {
            return [{
                label: 'OPENED',
                value: 'OPENED'
            }, {
                label: 'DEPARTED',
                value: 'DEPARTED'
            }];
        } else if (status === 'DEPARTED') {
            return [{
                label: selectedTruck.dockDoor ? 'AVAILABLE' : 'SELECT DOOR TO MARK AVAILABLE',
                value: 'AVAILABLE'
            }];
        }
        return []; // Return an empty array if no conditions match
    };

    const statusOptions = getStatusOptions(selectedTruck.status);

    // NEW: Disable DEPARTED option if no cargo or still loading
    const disabledStatusOptions = statusOptions.map(option => {
        if (option.value === 'DEPARTED') {
            const hasData = truckDataMap && Object.keys(truckDataMap).length > 0;
            return {
                ...option,
                isDisabled: isLoadingCargo || !hasData,
                label: isLoadingCargo 
                    ? 'Loading cargo...' 
                    : !hasData 
                        ? 'DEPARTED (No cargo data)' 
                        : option.label
            };
        }
        return option;
    });

    const handleUpdateDoor = (option: SelectOption) => {
        updateTruckDoor({ dockDoor: option.value ? Number(option.value) : null })
    }

    const handleUpdateStatus = (option: SelectOption) => {
        if (option.value === 'AVAILABLE' && !selectedTruck.dockDoor) {
            notify('Select a DOOR to make truck AVAILABLE', 'error');
            return;
        }
        updateTruck({ status: String(option.value) })
    }

    return (
        <div>
            <FlexContainer>
                <h3>{selectedTruck.name} - {formatEmail(cargoData.loadedBy)}</h3>
                <FlexContainer>
                    <Select
                        options={doorOptions}
                        styles={selectDoorStyles}
                        value={selectedDoor}
                        onChange={option => handleUpdateDoor(option)}
                    />
                    <Select
                        options={disabledStatusOptions}
                        styles={selectStatusStyles}
                        value={{
                            label: `Status: ${selectedTruck.status}`,
                            value: selectedTruck.status
                        }}
                        onChange={option => handleUpdateStatus(option)}
                    />
                </FlexContainer>
            </FlexContainer>
            <VirtualTable
                data={data}
                mapping={[{
                    name: 'ULD',
                    value: 's_flight_uld'
                }, {
                    name: 'MAWB',
                    value: 's_mawb',
                    s_mawb: true
                }, {
                    name: 'HAWB',
                    value: 's_hawb'
                }, {
                    name: 'Pieces',
                    value: 'i_pieces',
                    number: true
                }, {
                    name: 'SHC',
                    value: 's_special_hanlding_code'
                }, {
                    name: 'Destination',
                    value: 's_destination'
                }]}
                numRows={10}
            />
        </div>
    )
}

export const LoadingUnitedRegistration = () => {
    return (
        <LoaderContainer>
            <div className={'text-center'}>
                <h1>Waiting for United Registration</h1>
                <ScaleLoader color={'#6fb327'} />
            </div>
        </LoaderContainer>
    );
}

const Container = styled.div`
    display: flex;
    gap: 25px;
`;

const TrucksContainer = styled.div`
    flex: 1;
`;

const TruckAwbsContainer = styled.div`
    flex: 3;
`;

const FlexContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 15px;
`;

const LoaderContainer = styled.div`
    height: calc(100vh - 250px);
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-around;
    position: absolute;
`;

const MailIcon = styled.i`
    font-size: 32px;
`;

const selectDoorStyles = {
    control: (provided, state) => ({
        ...provided,
        // height: '50px',  // Set the height of the select control
        width: '200px'   // Set the width of the select control
    })
};

const selectStatusStyles = {
    control: (provided, state) => ({
        ...provided,
        // height: '50px',  // Set the height of the select control
        width: '300px'   // Set the width of the select control
    })
};
